<template>
  <div class="form-page-container">
    <PremiumPage
      title="Add Notice"
      subtitle="Broadcast important information to society members"
      showBack
      show-footer-buttons
      submit-label="Submit"
      :loading="loading"
      @cancel="$router.go(-1)"
      @submit="saveNoticeDetail"
    >
      <div class="pa-4">
        <v-form>
          <div class="half-width-wrap">
            <v-row>
              <v-col cols="12">
                <PremiumInput
                  v-model="notice.title"
                  label="Title"
                  placeholder="Enter a descriptive title"
                  icon="mdi-format-title"
                  v-validate="'required'"
                  name="title"
                  :error="errors.first('title') || serverSideErrors.title"
                />
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <PremiumInput
                  v-model="notice.description"
                  label="Description"
                  placeholder="Details of the notice..."
                  multiline
                  rows="4"
                  resizable
                  v-validate="'required'"
                  name="description"
                  :error="errors.first('description') || serverSideErrors.description"
                />
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <PremiumDatePicker
                  v-model="notice.expiry_date"
                  label="Expiry Date"
                  placeholder="Select expiry date (optional)"
                  name="ExpiryDate"
                  :error="errors.first('ExpiryDate') || serverSideErrors.expiry_date"
                />
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <div class="premium-field-label mb-3">Attachments</div>
                <input
                  type="file"
                  ref="attachInput"
                  multiple
                  accept="image/*,.pdf"
                  hidden
                  @change="onAttachChange"
                />
                <!-- Drop Zone -->
                <div
                  class="attach-dropzone"
                  :class="{ 'attach-dropzone-active': dragging }"
                  @click="$refs.attachInput.click()"
                  @dragover.prevent="dragging = true"
                  @dragleave="dragging = false"
                  @drop.prevent="onDrop"
                >
                  <v-icon size="28" color="var(--tx3)">mdi-paperclip</v-icon>
                  <div class="dropzone-label">Click or drag files here</div>
                  <div class="dropzone-hint">JPG, PNG, PDF allowed</div>
                </div>

                <!-- Preview Grid -->
                <div v-if="attachPreviews.length" class="attach-preview-grid mt-3">
                  <div
                    v-for="(p, i) in attachPreviews"
                    :key="i"
                    class="attach-preview-item"
                    @click="openMediaViewer(i)"
                  >
                    <img v-if="p.isImage" :src="p.url" class="attach-preview-img" />
                    <div v-else class="attach-preview-pdf">
                      <v-icon size="32" color="#EF4444">mdi-file-pdf-box</v-icon>
                      <span class="attach-preview-name">{{ p.name }}</span>
                    </div>
                    <button class="attach-remove-btn" @click.stop="removeAttach(i)">
                      <v-icon size="14" color="white">mdi-close</v-icon>
                    </button>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-form>
      </div>
    </PremiumPage>

    <!-- Media Viewer -->
    <MediaViewer
      v-model="mediaDialog"
      :items="mediaItems"
      :index="mediaIndex"
    />
  </div>
</template>
<script>
import { mapMutations } from "vuex";
import moment from "moment";
import PremiumPage from "../../common/PremiumPage.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import PremiumDatePicker from "../../common/PremiumDatePicker.vue";
import MediaViewer from "../../common/MediaViewer.vue";

export default {
  components: { PremiumPage, PremiumInput, PremiumDatePicker, MediaViewer },
  data() {
    return {
      user: {},
      loading: false,
      member: {},
      serverSideErrors: {},
      notice: {
        title: "",
        description: "",
        picture: [],
        expiry_date: "",
        documents: [],
      },
      attachPreviews: [],
      dragging: false,
      menu: false,
      mediaDialog: false,
      mediaIndex: 0,
    };
  },
  computed: {
    today() {
      return moment().format("YYYY-MM-DD");
    },
    mediaItems() {
      return this.attachPreviews.map(p => ({
        url: p.url,
        type: p.isImage ? 'image' : 'document',
        name: p.name
      }));
    }
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    saveNoticeDetail() {
      let _self = this;
      this.$validator.validateAll().then((result) => {
        if (result) {
          _self.loading = true;
          let formData = new FormData();

          formData.append("title", this.notice.title);
          formData.append("description", this.notice.description);
          formData.append("picture", this.notice.picture);
          formData.append(
            "expiry_date",
            this.notice.expiry_date
              ? moment.utc(this.notice.expiry_date).valueOf()
              : "",
          );
          if (this.notice.documents) {
            for (let i = 0; i < this.notice.documents.length; i++) {
              formData.append("documents[]", this.notice.documents[i]);
            }
          }

          _self.$axios
            .post("/societyadmin/notices/", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
            .then((response) => {
              let data = response.data;

              _self.loading = false;
              _self.serverSideErrors = {};

              if (data.status) {
                this.setAlert({
                  showAlert: true,
                  message: data.message,
                  variant: "green accent-4",
                  timeout: 2000,
                });
                _self.$router.push({ path: "/societyadmin/notice" });
              } else {
                if (!data.errors) {
                  this.setAlert({
                    showAlert: true,
                    message: data.message,
                    variant: "red accent-3",
                    timeout: 2000,
                  });
                } else {
                  for (let i = 0; i < data.errors.length; i++) {
                    this.serverSideErrors[data.errors[i].field] =
                      data.errors[i].message;
                  }
                }
              }
            });
        }
      });
    },
    onAttachChange(e) {
      this.addFiles(Array.from(e.target.files));
      e.target.value = "";
    },
    onDrop(e) {
      this.dragging = false;
      this.addFiles(Array.from(e.dataTransfer.files));
    },
    addFiles(files) {
      files.forEach(file => {
        this.notice.documents.push(file);
        const isImage = file.type.startsWith('image/');
        if (isImage) {
          const url = URL.createObjectURL(file);
          this.attachPreviews.push({ url, name: file.name, isImage: true });
        } else {
          this.attachPreviews.push({ url: null, name: file.name, isImage: false });
        }
      });
    },
    removeAttach(i) {
      this.notice.documents.splice(i, 1);
      this.attachPreviews.splice(i, 1);
    },
    openMediaViewer(index) {
      this.mediaIndex = index;
      this.mediaDialog = true;
    },
  },
  mounted() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },
};
</script>

<style scoped>
.form-page-container {
  padding: 24px;
}

/* ── 50% width wrapper ── */
.half-width-wrap {
  max-width: 50%;
}
@media (max-width: 960px) {
  .half-width-wrap {
    max-width: 100%;
  }
}

.premium-field-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--tx3, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ── Custom Textarea ── */
::v-deep .premium-textarea .v-input__slot {
  border-radius: 12px !important;
  background: var(--surface2, #f8fafc) !important;
  border: 1px solid var(--border, #e2e8f0) !important;
  box-shadow: none !important;
}
::v-deep .premium-textarea fieldset {
  border: none;
}


/* ── Attachment Uploader ── */
.attach-dropzone {
  border: 2px dashed var(--border, #e2e8f0);
  border-radius: 14px;
  background: var(--surface2, #f8fafc);
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}
.attach-dropzone:hover {
  border-color: var(--brand);
  background: rgba(13,153,114,0.04);
}
.attach-dropzone-active {
  border-color: var(--brand) !important;
  background: rgba(13,153,114,0.06) !important;
}
.dropzone-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--tx2);
  margin-top: 4px;
}
.dropzone-hint {
  font-size: 11.5px;
  color: var(--tx3);
  font-weight: 500;
}

.attach-preview-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.attach-preview-item {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--border);
  flex-shrink: 0;
  background: var(--surface2);
}
.attach-preview-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.attach-preview-pdf {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px;
}
.attach-preview-name {
  font-size: 9px;
  color: var(--tx3);
  text-align: center;
  word-break: break-all;
  line-height: 1.2;
}
.attach-remove-btn {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0,0,0,0.55);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
}
.attach-remove-btn:hover { background: #EF4444; }
</style>
